import { Component } from "react";
import { AlertTriangle, RefreshCcw } from "lucide-react";
import { captureException } from "@/lib/observability";

/**
 * Per-page error boundary with an inline fallback UI.
 *
 * Use this around routes where a single page-level crash should NOT take
 * down the whole app (which would trigger the global Sentry error boundary
 * and show a generic "Something went wrong" screen).
 */
export default class PageErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    const scope = this.props.scope || "page";
    // Always log; Sentry is a no-op when disabled.
    console.error(`[PageErrorBoundary] ${scope}`, error, info);
    try {
      captureException(error, scope);
    } catch {
      /* swallow — observability must never crash the app */
    }
  }

  handleRetry = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;
    const msg = this.state.error?.message || "Unexpected error rendering this page.";
    return (
      <div
        data-testid="page-error-boundary"
        className="fcc-card p-8 max-w-2xl mx-auto text-center"
      >
        <div className="w-14 h-14 mx-auto rounded-2xl bg-amber-100 flex items-center justify-center">
          <AlertTriangle className="w-6 h-6 text-amber-700" />
        </div>
        <h2 className="font-display text-2xl text-[var(--fcc-ink)] mt-5">
          Something went wrong on this page
        </h2>
        <p className="text-sm text-[var(--fcc-ink-soft)] mt-3">
          {this.props.scope ? `(${this.props.scope})` : null} {msg}
        </p>
        <p className="text-xs text-[var(--fcc-muted)] mt-2">
          The rest of KidHQ is still fine — try reloading this page or pick another tab from the menu.
        </p>
        <button
          onClick={this.handleRetry}
          className="fcc-btn-primary mt-6"
        >
          <RefreshCcw className="w-4 h-4" /> Try again
        </button>
      </div>
    );
  }
}
